import React, { FC, useMemo } from "react";
import { Dialog } from "components/Dialog";
import { styled } from "@mui/material/styles";
import { Case } from "interfaces/case";
import {
  Alert,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ProjectType } from "interfaces/project";
import { useProjectQuery } from "hooks/queries/useProjectQuery";
interface AddToProjectDialogProps {
  isDialogOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedCases: Case[];
  selectedProjectId: number | null;
}

export const AddToProjectDialog: FC<AddToProjectDialogProps> = ({
  isDialogOpen,
  onClose,
  onConfirm,
  selectedCases,
  selectedProjectId
}) => {
  const {
    data: projectData,
    isSuccess: projectSuccess,
    fetchStatus: projectFetchStatus
  } = useProjectQuery(selectedProjectId);

  const casesWithoutProject = useMemo(() => {
    return selectedCases.filter(caseItem => !caseItem.project);
  }, [selectedCases]);

  const groupedCasesByProject = useMemo(() => {
    return selectedCases.reduce<Record<string, Case[]>>(
      (projectDict, caseItem) => {
        const projectName = caseItem.project_name;
        if (projectName) {
          if (!projectDict[projectName]) {
            projectDict[projectName] = [];
          }
          projectDict[projectName].push(caseItem);
        }
        return projectDict;
      },
      {}
    );
  }, [selectedCases]);

  const selectedCasesWithoutInspectionReport = useMemo(() => {
    return selectedCases.some(
      selectedCase =>
        !selectedCase.inspection_severity ||
        selectedCase.inspection_severity["name"] === "None"
    );
  }, [selectedCases]);

  return (
    <Dialog
      dialogId="dialog-add-cases-to-project"
      title={
        selectedCases.length > 1
          ? "Add Cases to Project"
          : "Add Case to Project"
      }
      size="medium"
      open={isDialogOpen && (projectSuccess || projectFetchStatus === "idle")}
      onClose={onClose}
      data-qa="CM-Form-Add-Case-To-Project"
      onConfirm={onConfirm}
    >
      {selectedCasesWithoutInspectionReport &&
        projectData?.project_type === ProjectType.REPAIR && (
          <Alert sx={{ mb: 1 }} severity="warning">
            {selectedCases.length > 1
              ? "Some of your selected cases do not have inspection reports."
              : "The selected case does not have an inspection report."}
          </Alert>
        )}

      {casesWithoutProject.length > 0 && (
        <>
          <Typography variant="h6">
            Cases being added to: {projectData?.name}
          </Typography>
          <List>
            {casesWithoutProject.map(caseItem => (
              <ListItem key={caseItem.id} dense sx={{ gap: 1 }}>
                <ListItemIcon sx={{ minWidth: 0 }}>
                  <StyledWarningIcon
                    severity={caseItem.inspection_severity}
                    projectType={projectData?.project_type}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography>
                      {`${caseItem.display_id} – ${caseItem.site_name} – ${caseItem.machine_name}`}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
            {!casesWithoutProject.length && (
              <ListItem sx={{ pl: 6 }} dense>
                <Typography>No new cases are being added.</Typography>
              </ListItem>
            )}
          </List>
        </>
      )}

      {casesWithoutProject.length > 0 &&
        Object.values(groupedCasesByProject).length > 0 && <Divider />}

      {Object.values(groupedCasesByProject).length > 0 && (
        <>
          <Typography variant="h6" sx={{ pt: 1 }}>
            Cases being moved to: {projectData?.name ?? "No Project"}
          </Typography>
          <List>
            {Object.keys(groupedCasesByProject).map(projectName => (
              <div key={projectName}>
                {groupedCasesByProject[projectName].map(caseItem => (
                  <ListItem key={caseItem.id} dense sx={{ gap: 1 }}>
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <StyledWarningIcon
                        severity={caseItem.inspection_severity}
                        projectType={projectData?.project_type}
                      />
                    </ListItemIcon>
                    <div>
                      <Typography variant="subtitle1">
                        <b>{`${projectName} \u2192 ${projectData?.name ??
                          "No Project"} `}</b>
                      </Typography>
                      <Typography>
                        {`${caseItem.display_id} – ${caseItem.site_name} – ${caseItem.machine_name}`}
                      </Typography>
                    </div>
                  </ListItem>
                ))}
              </div>
            ))}
            {Object.keys(groupedCasesByProject).length === 0 && (
              <ListItem sx={{ pl: 6 }} dense>
                <Typography>No cases are being moved.</Typography>
              </ListItem>
            )}
          </List>
        </>
      )}
    </Dialog>
  );
};

const StyledWarningIcon = styled(WarningAmberIcon)<{
  severity: any;
  projectType: ProjectType | undefined;
}>`
  visibility: ${props =>
    (!props.severity || props.severity["name"] === "None") &&
    props.projectType === ProjectType.REPAIR
      ? "visible"
      : "hidden"};
  color: #ed6c02;
`;
